import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Zap, Clock, FileText, Users, Pencil, Trash2, Copy, MoreVertical, Plus, Search, Loader2, Lock,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient as qc } from "@/lib/queryClient";
import type { EmailAutomation, EmailTemplate, ClientTypeRecord, UserProfile } from "@shared/schema";

interface AvailableSender {
  id: string;
  name: string;
  email: string;
  type: "user" | "connector";
}

export function EmailAutomationsPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });
  const isAdmin = profile?.role === "admin";
  const activeUserId = (user as any)?.id || profile?.userId;

  // ── Queries ──────────────────────────────────────────────────────────────
  const { data: automations = [], isLoading: automationsLoading } = useQuery<EmailAutomation[]>({
    queryKey: ["/api/email-automations"],
    enabled: isAdmin,
  });
  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: isAdmin,
  });
  const { data: clientTypesData = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: isAdmin,
  });
  const { data: availableSenders = [] } = useQuery<AvailableSender[]>({
    queryKey: ["/api/available-senders"],
    enabled: isAdmin,
  });
  const { data: allUsers = [] } = useQuery<UserProfile[]>({
    queryKey: ["/api/users"],
    enabled: isAdmin,
  });

  // ── Form state ────────────────────────────────────────────────────────────
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState<EmailAutomation | null>(null);
  const [name, setName] = useState("");
  const [triggerType, setTriggerType] = useState("BEFORE_EVENT_DATE");
  const [triggerOffset, setTriggerOffset] = useState("0");
  const [triggerUnit, setTriggerUnit] = useState("DAYS");
  const [sendTime, setSendTime] = useState("09:00");
  const [sendTimezone, setSendTimezone] = useState("CLIENT");
  const [templateId, setTemplateId] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [clientTypeIds, setClientTypeIds] = useState<string[]>([]);
  const [requireUnpaidInvoice, setRequireUnpaidInvoice] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/email-automations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: "Automation created" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to create automation.", variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiRequest("PATCH", `/api/email-automations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: "Automation updated" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to update automation.", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/email-automations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      setDeleteConfirmOpen(false);
      toast({ title: "Automation deleted" });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to delete automation.", variant: "destructive" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: (automation: EmailAutomation) => {
      const { id, createdAt, updatedAt, ...rest } = automation as any;
      return apiRequest("POST", "/api/email-automations", { ...rest, name: `${automation.name} (Copy)`, isActive: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-automations"] });
      toast({ title: "Automation duplicated", description: "Copy created and set to inactive." });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message || "Failed to duplicate.", variant: "destructive" }),
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const resetForm = () => {
    setEditingAutomation(null);
    setName("");
    setTriggerType("BEFORE_EVENT_DATE");
    setTriggerOffset("0");
    setTriggerUnit("DAYS");
    setTemplateId("");
    setIsActive(true);
    setAssignedToUserId("");
    setSendTime("09:00");
    setSendTimezone("CLIENT");
    setRequireUnpaidInvoice(false);
    setClientTypeIds([]);
  };

  const openEdit = (automation: EmailAutomation) => {
    setEditingAutomation(automation);
    setName(automation.name);
    setTriggerType(automation.triggerType);
    setTriggerOffset(String(automation.triggerOffsetValue));
    setTriggerUnit(automation.triggerOffsetUnit);
    setTemplateId(automation.templateId);
    setIsActive(automation.isActive ?? true);
    setAssignedToUserId(automation.assignedToUserId || "");
    setSendTime(automation.sendTime || "09:00");
    setSendTimezone(automation.sendTimezone || "CLIENT");
    setRequireUnpaidInvoice(automation.requireUnpaidInvoice ?? false);
    setClientTypeIds(((automation as any).clientTypeIds as string[] | null | undefined) || []);
    setIsDialogOpen(true);
  };

  const handleSave = () => {
    const data = {
      name,
      triggerType,
      triggerOffsetValue: parseInt(triggerOffset) || 0,
      triggerOffsetUnit: triggerUnit,
      audienceType: "CLIENT",
      audienceValue: null,
      templateId,
      isActive,
      sendTime,
      sendTimezone,
      requireUnpaidInvoice,
      scope: "ADMIN_PRIVATE",
      ownerUserId: user?.id,
      assignedToUserId: assignedToUserId && assignedToUserId !== "none" ? assignedToUserId : null,
      clientTypeIds,
    };
    if (editingAutomation) {
      updateMutation.mutate({ id: editingAutomation.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const canEdit = (a: EmailAutomation) => isAdmin || (a.scope === "USER" && a.ownerUserId === activeUserId);

  const filtered = automations.filter(a =>
    !searchQuery || a.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <Lock className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p>Admin access required</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 w-full overflow-auto">
      <div>
        <h2 className="text-lg font-semibold">Email Automations</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Automated emails sent before or after event dates. Only fired on unpaid, non-cancelled invoices.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search automations…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-automations-admin"
          />
        </div>
        <Button
          onClick={() => { resetForm(); setIsDialogOpen(true); }}
          data-testid="button-new-automation-admin"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Automation
        </Button>
      </div>

      {automationsLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-6 text-center text-muted-foreground">
          <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>{searchQuery ? "No automations match your search" : "No automations created yet"}</p>
          {!searchQuery && (
            <p className="text-sm mt-1">Create automations to send emails before or after event dates</p>
          )}
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map(automation => {
            const tpl = templates.find(t => t.id === automation.templateId);
            return (
              <Card key={automation.id} className="hover-elevate" data-testid={`card-automation-${automation.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        {automation.name}
                        <Badge variant={automation.isActive ? "default" : "secondary"}>
                          {automation.isActive ? "Active" : "Paused"}
                        </Badge>
                      </CardTitle>
                    </div>
                    {canEdit(automation) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`automation-menu-admin-${automation.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(automation)}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => duplicateMutation.mutate(automation)}
                            disabled={duplicateMutation.isPending}
                          >
                            <Copy className="w-4 h-4 mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => { setDeleteId(automation.id); setDeleteConfirmOpen(true); }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 shrink-0" />
                      <span>
                        {automation.triggerOffsetValue === 0
                          ? "Day of event"
                          : `${automation.triggerOffsetValue} days ${automation.triggerType === "BEFORE_EVENT_DATE" ? "before" : "after"} event`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 shrink-0" />
                      <span>
                        Send at {automation.sendTime || "09:00"}{" "}
                        ({automation.sendTimezone === "CLIENT" ? "client's timezone" : (automation.sendTimezone || "").replace("America/", "")})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 shrink-0" />
                      <span>Template: {tpl?.name || "Unknown"}</span>
                    </div>
                    {automation.assignedToUserId && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 shrink-0" />
                        <span>
                          From:{" "}
                          {automation.assignedToUserId === "CLIENT_REP"
                            ? "Client's Rep"
                            : (() => {
                                const sender = availableSenders.find(s => s.id === automation.assignedToUserId);
                                if (sender) return `${sender.name} (${sender.email})`;
                                const u = allUsers.find(u => u.userId === automation.assignedToUserId);
                                return u?.gmailConnectedEmail || automation.assignedToUserId;
                              })()}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingAutomation ? "Edit Automation" : "Create Automation"}</DialogTitle>
            <DialogDescription>
              {editingAutomation
                ? "Update your email automation"
                : "Set up an automated email to send before or after event dates"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Automation Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., 59-Day Payment Reminder"
                data-testid="input-automation-name-admin"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trigger</Label>
                <Select value={triggerType} onValueChange={setTriggerType}>
                  <SelectTrigger data-testid="select-trigger-type-admin">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BEFORE_EVENT_DATE">Before Event Date</SelectItem>
                    <SelectItem value="AFTER_EVENT_DATE">After Event Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Days {triggerType === "BEFORE_EVENT_DATE" ? "Before" : "After"}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={triggerOffset}
                    onChange={(e) => setTriggerOffset(e.target.value)}
                    className="w-24"
                    min="0"
                    data-testid="input-trigger-offset-admin"
                  />
                  <span className="text-sm text-muted-foreground">
                    {triggerOffset === "0" ? "(day of event)" : "days"}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Send Time</Label>
                <Input
                  type="time"
                  value={sendTime}
                  onChange={(e) => setSendTime(e.target.value)}
                  data-testid="input-send-time-admin"
                />
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select value={sendTimezone} onValueChange={setSendTimezone}>
                  <SelectTrigger data-testid="select-send-timezone-admin">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLIENT">Client's Address Timezone</SelectItem>
                    <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                    <SelectItem value="America/Phoenix">Arizona (MST)</SelectItem>
                    <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Hawaii Time (HST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger data-testid="select-automation-template-admin">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Send Email From</Label>
              <Select value={assignedToUserId} onValueChange={setAssignedToUserId}>
                <SelectTrigger data-testid="select-assign-user-admin">
                  <SelectValue placeholder="Select sender">
                    {assignedToUserId === "CLIENT_REP"
                      ? "Client's Assigned Rep"
                      : assignedToUserId && assignedToUserId !== "none"
                        ? (() => {
                            const sender = availableSenders.find(s => s.id === assignedToUserId);
                            if (sender) return `${sender.name} (${sender.email})`;
                            const u = allUsers.find(u => u.id === assignedToUserId);
                            return u ? `${u.userId} ${u.gmailConnectedEmail ? `(${u.gmailConnectedEmail})` : ""}`.trim() : "Select sender";
                          })()
                        : "Select sender"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assignment</SelectItem>
                  <SelectItem value="CLIENT_REP">Client's Assigned Rep</SelectItem>
                  {availableSenders.filter(s => s.type === "connector").map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                  ))}
                  {availableSenders.filter(s => s.type === "user").map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                  ))}
                  {allUsers.filter(u => !availableSenders.some(s => s.id === u.id)).map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email || u.id} {u.gmailConnectedEmail ? `(${u.gmailConnectedEmail})` : "(no email connected)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Limit to Client Types</Label>
              <div className="border rounded-md p-2 space-y-1 max-h-40 overflow-auto">
                {clientTypesData.length === 0 && (
                  <p className="text-xs text-muted-foreground px-1 py-1">No client types configured.</p>
                )}
                {clientTypesData.map(ct => (
                  <label
                    key={ct.id}
                    className="flex items-center gap-2 text-sm px-1 py-1 rounded hover:bg-muted cursor-pointer"
                    data-testid={`row-automation-clienttype-admin-${ct.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={clientTypeIds.includes(ct.id)}
                      onChange={() =>
                        setClientTypeIds(prev =>
                          prev.includes(ct.id) ? prev.filter(x => x !== ct.id) : [...prev, ct.id]
                        )
                      }
                    />
                    {ct.name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to send to all client types.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="adminRequireUnpaid"
                checked={requireUnpaidInvoice}
                onChange={(e) => setRequireUnpaidInvoice(e.target.checked)}
                className="rounded"
                data-testid="checkbox-require-unpaid-admin"
              />
              <Label htmlFor="adminRequireUnpaid">Only if invoice is not marked paid</Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="adminAutomationActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="adminAutomationActive">Active</Label>
            </div>

            {(() => {
              const offset = parseInt(triggerOffset) || 0;
              const isBefore = triggerType === "BEFORE_EVENT_DATE";
              const [hStr, mStr] = (sendTime || "09:00").split(":");
              const h = parseInt(hStr) || 0;
              const m = parseInt(mStr) || 0;
              const hour = h % 12 || 12;
              const ampm = h < 12 ? "AM" : "PM";
              const timeStr = `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
              const tzLabel =
                sendTimezone === "CLIENT" ? "client's timezone"
                : sendTimezone === "America/Chicago" ? "CT"
                : sendTimezone === "America/New_York" ? "ET"
                : sendTimezone === "America/Denver" ? "MT"
                : sendTimezone === "America/Los_Angeles" ? "PT"
                : sendTimezone === "America/Phoenix" ? "MST"
                : sendTimezone === "America/Anchorage" ? "AKT"
                : sendTimezone === "Pacific/Honolulu" ? "HST"
                : sendTimezone;
              const whenStr =
                offset === 0
                  ? "on the day of each event"
                  : `${offset} day${offset === 1 ? "" : "s"} ${isBefore ? "before" : "after"} each event`;
              return (
                <div
                  className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md space-y-1"
                  data-testid="automation-timing-preview"
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                    Preview
                  </p>
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    This email sends{" "}
                    <strong>{whenStr}</strong> at{" "}
                    <strong>{timeStr} ({tzLabel})</strong>.
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Paid and cancelled invoices are always skipped.
                  </p>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!name || !templateId || createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-automation-admin"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {editingAutomation ? "Update Automation" : "Create Automation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Automation?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-automation-admin"
            >
              {deleteMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
